'use client';

import '@/lib/polyfills/browser-polyfills';

import { useEffect } from 'react';

import { CrossTabSyncHandler } from '@/components/assistant-ui/cross-tab-sync-handler';
import { HitlProvider } from '@/components/assistant-ui/hitl/hitl-provider';
import { ToolGroupProvider } from '@/components/assistant-ui/tool-group-provider';
import { AccessibilityPluginSync } from '@/components/providers/accessibility-plugin-sync';
import { ConfigProvider, useConfig } from '@/components/providers/config-provider';
import { CrossTabSyncProvider } from '@/components/providers/cross-tab-sync-provider';
import {
  MessageDataComponentsProvider,
  type MessageDataComponents,
} from '@/components/providers/data-renderers-provider';
import { DebugModeProvider } from '@/components/providers/debug-mode-provider';
import { DevToolsProvider } from '@/components/providers/dev-tools-provider';
import { PlatformProvider } from '@/components/providers/platform-provider';
import { ReadOnlyProvider } from '@/components/providers/read-only-provider';
import {
  ReadOnlyRuntimeProvider,
  type ConversationMessage,
  type ConversationThread,
} from '@/components/providers/read-only-runtime-provider';
import { StorageAccessProvider } from '@/components/providers/storage-access-provider';
import { TriggerContextProvider } from '@/components/providers/trigger-context-provider';
import { WidgetAnalyticsProvider } from '@/components/providers/widget-analytics-provider';
import { SkipToContent } from '@/components/ui/skip-to-content';
import { useOnMountCallback } from '@/hooks/use-on-mount-callback';
import { CloudLocalRuntimeProvider } from '@/lib/assistant-cloud-local';
import type { ClientSafeConfig } from '@/lib/types/tenant';
import { initStandaloneAffiliate } from '@/lib/utils/standalone-affiliate';

import { Layout } from './bazak-assistant/layout';

function StandaloneAffiliateInit() {
  const config = useConfig();
  const standaloneAffiliateId = config.navigation?.affiliateConfig?.standaloneAffiliateId;

  useEffect(() => {
    initStandaloneAffiliate(standaloneAffiliateId);
  }, [standaloneAffiliateId]);

  return null;
}

export type AssistantMode = 'embed';

export interface BazakAssistantProps {
  mode?: AssistantMode;
  clientConfig?: ClientSafeConfig;
  resolvedParentContext?: Record<string, unknown>;
  widgetVersion?: string;
  debug?: boolean;
  disableAnalytics?: boolean;
  /** Load the thread list (triggers read-only mode — no composer) */
  onLoadThreads?: () => Promise<ConversationThread[]>;
  onLoadMessages?: (threadId: string) => Promise<ConversationMessage[]>;
  onReady?: () => void;
  defaultSidebarOpen?: boolean;
  onSidebarOpenChange?: (open: boolean) => void;
  onActiveThreadChange?: (threadId: string) => void;
  hideSidebar?: boolean;
  /** Host components, keyed by data-part name, that render data the host bakes
   *  onto messages (see {@link ConversationMessage.dataParts}). */
  dataComponents?: MessageDataComponents;
}

export function BazakAssistant({
  clientConfig,
  mode,
  resolvedParentContext,
  widgetVersion,
  debug,
  disableAnalytics,
  onLoadThreads,
  onLoadMessages,
  onReady,
  defaultSidebarOpen,
  onSidebarOpenChange,
  onActiveThreadChange,
  hideSidebar,
  dataComponents,
}: BazakAssistantProps) {
  const isWidget = mode === 'embed';
  const platform = isWidget ? 'copilot' : 'app';
  const isReadOnly = !!onLoadThreads;

  // Read-only mode defers onReady to ReadOnlyRuntimeProvider (after data loads).
  useOnMountCallback(isReadOnly ? undefined : onReady);

  const layoutProps = { mode, defaultSidebarOpen, onSidebarOpenChange, hideSidebar };

  const runtimeContent = isReadOnly ? (
    <ReadOnlyRuntimeProvider
      onLoadThreads={onLoadThreads!}
      onLoadMessages={onLoadMessages}
      onReady={onReady}
      onActiveThreadChange={onActiveThreadChange}
    >
      <Layout {...layoutProps} />
    </ReadOnlyRuntimeProvider>
  ) : (
    <CloudLocalRuntimeProvider platform={platform}>
      <CrossTabSyncHandler />
      <Layout {...layoutProps} />
    </CloudLocalRuntimeProvider>
  );

  const content = (
    <MessageDataComponentsProvider dataComponents={dataComponents}>
      <PlatformProvider platform={platform}>
        <WidgetAnalyticsProvider
          isWidget={isWidget && !disableAnalytics && !isReadOnly}
          widgetVersion={widgetVersion}
          resolvedParentContext={resolvedParentContext}
        >
          <TriggerContextProvider>
            <SkipToContent />
            {isWidget && <AccessibilityPluginSync />}
            {!isWidget && !isReadOnly && <StandaloneAffiliateInit />}
            <DebugModeProvider initialDebugMode={debug}>
              <DevToolsProvider>
                <ReadOnlyProvider readOnly={isReadOnly}>
                  {isReadOnly ? (
                    <ToolGroupProvider>
                      <HitlProvider>{runtimeContent}</HitlProvider>
                    </ToolGroupProvider>
                  ) : (
                    <StorageAccessProvider>
                      <CrossTabSyncProvider>
                        <ToolGroupProvider>
                          <HitlProvider>{runtimeContent}</HitlProvider>
                        </ToolGroupProvider>
                      </CrossTabSyncProvider>
                    </StorageAccessProvider>
                  )}
                </ReadOnlyProvider>
              </DevToolsProvider>
            </DebugModeProvider>
          </TriggerContextProvider>
        </WidgetAnalyticsProvider>
      </PlatformProvider>
    </MessageDataComponentsProvider>
  );

  if (clientConfig) {
    return <ConfigProvider config={clientConfig}>{content}</ConfigProvider>;
  }

  return content;
}
